import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ReactTooltip from 'react-tooltip'
import { useSelector, useDispatch } from 'react-redux'
import { Autocomplete, Divider, MenuItem, IconButton, TextField } from '@mui/material'
import { ArrowBack, Search } from '@mui/icons-material'
import _ from 'lodash'

import backend from 'utils/backend'
import { useDebounce, useOutsideRef } from 'hooks'
import { selectFacets } from 'store/actions/facets'
import { changeSearchText, searchFundersByParams } from 'store/actions/search'
import { serializeSearchParams } from 'components/SearchParams/helpers'
import { selectflatSearchFacets } from 'store/selectors/facets'
import { selectSearchText } from 'store/selectors/search'
import { resetFacetFilters } from 'store/actions/filters'
import {
  trackSearchAutocompleteFacetClick,
  trackSearchAutocompleteFunderClick
} from 'utils/mixpanel'

const SearchForm = ({ gotoFunder, mobile = false }) => {
  const dispatch = useDispatch()
  const { i18n } = useTranslation()
  const t = i18n.getResourceBundle(i18n.language)
  const searchText = useSelector(selectSearchText)
  const flatSearchFacets = useSelector(selectflatSearchFacets)

  const [showSearchForm, setShowSearchForm] = useState(false)
  const [localSearchText, setLocalSearchText] = useState(searchText || '')
  const debouncedSearchTerm = useDebounce(localSearchText, 500)
  const [filteredFacets, setFilteredFacets] = useState([])
  const [localResults, setLocalResults] = useState([])

  const autocompleteStyle = mobile
    ? 'Header-search-mobile-field-container'
    : 'Header-search-field-container'

  const inputRef = useRef(null)

  useEffect(() => setLocalSearchText(searchText || ''), [searchText])

  useEffect(() => {
    const getQuickResults = async searchText => {
      const searchParams = serializeSearchParams({
        searchText,
        pageNumber: 1,
        viewsPerPage: 5,
        sortBy: 'match',
        searchFields: ['name', 'alias']
      })
      const { data: { results } = {} } = await backend.get(`api/search/funders/${searchParams}`)
      setLocalResults(
        results.map(item => ({
          text: item.alias ? `${item.name} (${item.alias})` : item.name,
          value: { funder: item.id }
        }))
      )
    }

    const getFilteredFacets = searchText => {
      setFilteredFacets(
        flatSearchFacets
          .filter(item => item.title.toLowerCase().includes(searchText.toLowerCase()))
          .slice(0, 5)
          .map(item => ({
            text: `${item.title} ${t.global.in} ${_.get(t, item.parent)}`,
            value: {
              facet: item.id,
              facetType: item.facetType,
              parentFacetIds: item.parentFacetIds
            }
          }))
      )
    }

    if (debouncedSearchTerm && debouncedSearchTerm.length >= 3) {
      getFilteredFacets(debouncedSearchTerm)
      getQuickResults(debouncedSearchTerm)
    } else {
      setFilteredFacets([])
      setLocalResults([])
    }
  }, [debouncedSearchTerm, flatSearchFacets])

  const handleFormSubmit = () => {
    // Needed to hide the autocomplete popover when clicking on the keyword search button
    inputRef.current.blur()

    // full text search
    dispatch(changeSearchText(localSearchText))
    dispatch(resetFacetFilters())
    dispatch(searchFundersByParams('SEARCH_FORM'))

    setLocalResults([])
    setFilteredFacets([])
  }

  const handleInputChange = (event, value) => {
    setLocalSearchText(value)
  }

  const handleNewRequest = async (event, item) => {
    if (!_.has(item, 'value')) {
      handleFormSubmit()
      return
    }

    if (_.has(item.value, 'funder')) {
      gotoFunder(item.value.funder)
      await trackSearchAutocompleteFunderClick(item.value.funder)
    } else if (_.has(item.value, 'facet')) {
      dispatch(changeSearchText(''))
      dispatch(resetFacetFilters())
      dispatch(selectFacets([{ id: item.value.facet, facetType: item.value.facetType }]))
      dispatch(searchFundersByParams())
      await trackSearchAutocompleteFacetClick({
        id: item.value.facet,
        facetType: item.value.facetType
      })
    }

    setLocalResults([])
    setFilteredFacets([])
  }

  const [formHighlighted, setFormHighlighted, formRef] = useOutsideRef()

  return (
    <div>
      {((showSearchForm && mobile) || !mobile) && (
        <form className="Header-search" ref={formRef}>
          {mobile && (
            <IconButton onClick={e => setShowSearchForm(false)}>
              <ArrowBack />
            </IconButton>
          )}
          {/* Autocomplete Field */}
          <div className={autocompleteStyle}>
            <Autocomplete
              data-tip={t.search.toolTip}
              fullWidth
              freeSolo
              disableClearable
              blurOnSelect
              className={`Header-search__autocomplete ${formHighlighted}`}
              slotProps={{ popper: { className: 'Header-search__dropdown' } }}
              openOnFocus={true}
              options={
                ((localResults.length || filteredFacets.length) && [
                  ...filteredFacets,
                  ...(filteredFacets.length && localResults.length
                    ? [{ text: 'divider', elem: <Divider /> }]
                    : []),
                  ...localResults,
                  {
                    text: localSearchText,
                    elem: (
                      <MenuItem onClick={handleFormSubmit} className="extra">
                        <div className="extra--text">
                          <div>{t.search.extraMenuItem}</div>
                          <div>{t.search.extraMenuItemSecond}</div>
                        </div>
                        <div className="extra--item">{t.search.go}</div>
                      </MenuItem>
                    )
                  }
                ]) ||
                []
              }
              getOptionLabel={opt => opt.text || ''}
              renderOption={({ key, ...props }, opt) => (
                <React.Fragment key={key}>
                  {opt.elem || <MenuItem {...props}>{opt.text}</MenuItem>}
                </React.Fragment>
              )}
              onFocus={() => setFormHighlighted('highlight')}
              filterOptions={key => key}
              inputValue={localSearchText}
              onInputChange={handleInputChange}
              onChange={handleNewRequest}
              renderInput={params => (
                <TextField
                  {...params}
                  inputRef={inputRef}
                  variant="standard"
                  placeholder={t.search.hintText}
                />
              )}
            />
            <ReactTooltip place="bottom" effect="solid" />
          </div>

          <IconButton id="Header-submit" type="submit" className="Header-search__submit">
            <Search />
          </IconButton>
        </form>
      )}
      {!showSearchForm && mobile && (
        <IconButton className="Header-search__open-search" onClick={e => setShowSearchForm(true)}>
          <Search />
        </IconButton>
      )}
    </div>
  )
}

export default SearchForm
